import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';

const QRCodeScreen = ({ onSubmit, userId = 1, reviewUrl }) => {
  const [qrCodeDataURL, setQrCodeDataURL] = useState('');
  const [timeLeft, setTimeLeft] = useState(90);
  const [timerActive, setTimerActive] = useState(true);

  useEffect(() => {
    // Generate QR code
    const generateQRCode = async () => {
      try {
        // Use IP address instead of localhost for cross-device scanning
        const getServerUrl = () => {
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            return `http://10.1.10.213:${window.location.port || 3001}`;
          }
          return window.location.origin;
        };
        
        const qrData = `${getServerUrl()}/scanned?user_id=${userId}`;
        const dataURL = await QRCode.toDataURL(qrData, {
          width: 200,
          margin: 2,
          color: {
            dark: '#333333',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataURL(dataURL);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [userId]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      // Auto-skip when timer reaches 0
      setTimerActive(false);
      onSubmit('auto-timeout');
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft, onSubmit]);

  const handleSkip = () => {
    setTimerActive(false);
    onSubmit('skipped');
  };


  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, delay: 0.2 }
    }
  };

  const qrVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: "spring", 
        stiffness: 200, 
        damping: 10,
        delay: 0.4
      }
    }
  };

  return (
    <motion.div 
      className="qr-code-screen"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="qr-code-layout">
        {/* Left Column - QR Code */}
        <motion.div 
          className="qr-code-container"
          variants={qrVariants}
          initial="hidden"
          animate="visible"
        >
          {qrCodeDataURL && (
            <img 
              src={qrCodeDataURL} 
              alt="QR Code for Google review"
              className="qr-code-image"
            />
          )}
          
          <motion.p
            className="scan-hint"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            Use your phone's camera to scan the QR code for Google review, or skip to continue
          </motion.p>
        </motion.div>

        {/* Right Column - Content */}
        <div className="qr-code-content">
          <motion.h2
            className="qr-title-with-emoji"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <span className="qr-emoji">😊</span>
            Great to hear you're happy!
          </motion.h2>
          
          <motion.p
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            Would you like to scan the QR code to submit an honest Google review?
          </motion.p>
          
          <motion.div 
            className="timer-display"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
          >
            <p className="timer-label">Auto-skip in {timeLeft} seconds</p>
          </motion.div>
          
          <div className="qr-actions">
            <motion.button 
              className="skip-button"
              onClick={handleSkip}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              variants={contentVariants}
              initial="hidden"
              animate="visible"
            >
              Skip Now
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default QRCodeScreen;